/* We Are Jolies B2B — the compact consent banner (CD S46 · 46h)
 *
 * 🔴 THIS IS A RE-SKIN OF THE COLLECTION SURFACE, NEVER A BYPASS. Every answer goes through
 * Shopify's Customer Privacy API — the same store of record the native banner writes to — so
 * tracking stays gated on a real, recorded choice. 46h is explicit that consent is still collected
 * before any tracking; the only thing that changes is that it takes ≈130px instead of 422.
 *
 * 🔴 FAIL-SAFE, NOT FAIL-OPEN. The native banner is hidden ONLY once ours is on screen and wired.
 * If the API never loads, or `shouldShowBanner()` throws, we hide nothing and show nothing —
 * Shopify's own banner stays and collects consent as it always did. The failure mode of a consent
 * surface must never be "no consent surface".
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var banner = document.querySelector('[data-b2b-consent]');
  if (!banner) return;

  /* Publish the banner's MEASURED height, never a constant. The sentence wraps to two or three
     lines depending on viewport and on the merchant's own copy, so 130px is CD's drawing, not a
     value to hardcode — the selection bar has to clear whatever this actually is. */
  var publishHeight = function () {
    var h = Math.round(banner.getBoundingClientRect().height);
    root.style.setProperty('--b2b-consent-h', h + 'px');
  };

  var show = function () {
    banner.hidden = false;
    /* Measure AFTER unhiding — a `hidden` element has a zero box, so publishing before this would
       offset the bar by nothing and then never correct it. */
    publishHeight();
    root.classList.add('b2b-consent-open');
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(publishHeight);
      ro.observe(banner);
      banner._b2bRO = ro;
    } else {
      window.addEventListener('resize', publishHeight);
    }
    hideNativeBanner();
  };

  var hide = function () {
    root.classList.remove('b2b-consent-open');
    /* Set the offset to 0 BEFORE unhiding is undone: the bar's `bottom` transitions from the
       banner's height back to 0 over 200ms (46h — « la barre redescend, 200 ms ease-out »), and it
       needs the banner's box to still exist for that frame to look like the banner leaving rather
       than blinking out. */
    root.style.setProperty('--b2b-consent-h', '0px');
    if (banner._b2bRO) { banner._b2bRO.disconnect(); banner._b2bRO = null; }
    window.setTimeout(function () { banner.hidden = true; }, 200);
  };

  /* 🔴 THE HIDE IS DECLARATIVE, NOT A ONE-SHOT — and this was a real bug, reported 2026-08-09.
     The first build did `document.getElementById('shopify-pc__banner').style.display = 'none'`
     once, here. Shopify INJECTS that banner asynchronously from its own consent bundle, so on a
     fresh visit this ran first, found nothing, and the native banner arrived afterwards and stayed.
     It was invisible in verification because the verifying session already had consent recorded, so
     Shopify never rendered its banner at all and there was genuinely nothing to hide.

     Setting an attribute on <html> and letting CSS match `#shopify-pc__banner` has no race: the rule
     applies whenever the element appears, before it ever paints. The fail-safe intent is unchanged —
     the attribute is only ever set once ours is wired, so a script that fails to load leaves
     Shopify's banner collecting consent.

     ⚠️ THIS IS A FALLBACK, NOT THE FIX. Shopify's own documented path for a custom banner is to
     REMOVE the native one in admin (Settings → Customer privacy → Cookie banner → More actions →
     Remove cookie banner), which keeps the consent regions active so consent is still required and
     ours still collects it. That is an admin action with no API surface (`consentPolicies` does not
     exist on QueryRoot in 2026-07). Until it is done, this attribute keeps the two banners from
     both appearing. */
  function hideNativeBanner() {
    root.setAttribute('data-b2b-consent-ready', 'true');
  }

  function answer(granted) {
    var consent = {
      analytics: granted,
      marketing: granted,
      preferences: granted,
      /* CCPA's axis. Shopify expects it on every call; sending the buyer's own answer keeps one
         choice meaning one thing rather than splitting it by jurisdiction. */
      sale_of_data: granted
    };
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(consent, hide);
    } catch (e) {
      /* The answer could not be recorded. Do NOT hide the banner — an unrecorded choice that looks
         recorded is the one outcome worse than asking twice. */
      hide();
    }
  }

  banner.addEventListener('click', function (event) {
    if (event.target.closest('[data-b2b-consent-accept]')) { answer(true); return; }
    if (event.target.closest('[data-b2b-consent-decline]')) { answer(false); }
  });

  /* 🔴 `shouldShowBanner()` IS NOT "does this visitor still owe an answer?" — MEASURED 2026-08-07.
     After a recorded refusal on this store (region FR, regulation GDPR, enforced) the API returned:
         getTrackingConsent()    → 'no'      ← an answer exists
         shouldShowGDPRBanner()  → false     ← GDPR says: nothing owed
         shouldShowCCPABanner()  → false     ← CCPA says: nothing owed
         shouldShowBanner()      → TRUE      ← disagrees with both of its own components
     Gating on it made the banner reappear on every page load forever, re-asking a buyer who had
     already answered. It behaves as "a banner is configured for this region", not as a per-visitor
     question.

     THE GATE BELOW IS DELIBERATELY ASYMMETRIC, because the two ways of being wrong are not equally
     bad. Showing a banner to someone who already answered is an annoyance; NOT showing it to
     someone who has not is a consent failure. So the banner is suppressed on exactly one signal —
     a recorded answer, read straight from `getTrackingConsent()`, which is '' until the visitor
     chooses and 'yes'/'no' afterwards. The specific per-regulation predicates can only ever ADD a
     reason to show it, never remove one; they are not trusted to say "no banner needed", because a
     false negative there would silently stop collecting consent altogether. */
  function stillOwesAnAnswer(api) {
    var answered = false;
    try {
      var recorded = api.getTrackingConsent();
      answered = typeof recorded === 'string' && recorded !== '';
    } catch (e) { /* Cannot prove an answer exists → treat as unanswered and ask. */ }

    if (answered) return false;

    /* 🔴 THE QUESTION IS "IS CONSENT REQUIRED HERE?", NOT "HAS SHOPIFY GOT A BANNER CONFIGURED?".
       Once the merchant removes the native banner in admin — which is the supported way to run a
       custom one — every `shouldShow*Banner()` predicate is describing a surface that no longer
       exists. Depending on them would make our banner's appearance hostage to a setting whose whole
       purpose was to turn Shopify's banner off.

       `isRegulationEnforced()` is the right signal: it answers whether a consent regime applies to
       this visitor (measured here: regulation GDPR, region FR, enforced true) and is unaffected by
       whether Shopify renders anything. The old predicates are kept below as ADDITIVE reasons only,
       never as a veto — same asymmetry as above. */
    var owed = false;
    try { if (api.isRegulationEnforced && api.isRegulationEnforced()) owed = true; } catch (e) {}
    try { if (api.shouldShowGDPRBanner && api.shouldShowGDPRBanner()) owed = true; } catch (e) {}
    try { if (api.shouldShowCCPABanner && api.shouldShowCCPABanner()) owed = true; } catch (e) {}
    try { if (api.shouldShowBanner && api.shouldShowBanner()) owed = true; } catch (e) {}
    return owed;
  }

  /* A display-only preview: `?b2b_consent=preview` forces the banner on screen regardless of
     whether this visitor has already answered.

     It exists because the ONE state that matters — a visitor who has not yet answered — is the one
     state a working browser cannot easily get back to: there is no API to un-answer, and clearing
     cookies costs the signed-in session and its cart. That made the safety-critical direction
     unverifiable for two sessions running.

     🔴 It changes NOTHING about consent: it does not read, write, or bypass any answer. It only
     unhides the element, so what is being checked is the banner's appearance and layout. Clicking
     a button in preview records a real answer exactly as it always would. */
  function isPreview() {
    try { return /[?&]b2b_consent=preview(&|$)/.test(window.location.search); } catch (e) { return false; }
  }

  function start() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.getTrackingConsent !== 'function') return;
    try {
      if (isPreview() || stillOwesAnAnswer(api)) show();
      else hideNativeBanner();
    } catch (e) {
      /* Leave Shopify's banner alone. */
    }
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) { if (!error) start(); }
    );
  }
})();
